import { z } from "zod";

/**
 * One time slot inside an availability — `HH:MM` 24h strings.
 */
export const availabilityTimeSchema = z.object({
  /** Present on existing rows; absent on new ones. */
  id: z.string().uuid().optional(),
  start: z.string().regex(/^\d{2}:\d{2}(?::\d{2})?$/, "expected HH:MM"),
  end: z.string().regex(/^\d{2}:\d{2}(?::\d{2})?$/, "expected HH:MM"),
});
export type AvailabilityTime = z.infer<typeof availabilityTimeSchema>;

/** Day-of-week strings as stored in `availabilities.day`. */
export const DAYS_OF_WEEK = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
] as const;
export type DayOfWeek = (typeof DAYS_OF_WEEK)[number];

/**
 * Mode discriminator captured in DB columns:
 *   - `allDays = true`                                  → "all-days" (one schedule for every day)
 *   - `day` set, `allDays = false`, `dateCustom = null` → "weekly"   (per day-of-week)
 *   - `dateCustom` set                                  → "custom"   (specific calendar date)
 */
export const availabilityDtoSchema = z.object({
  id: z.string().uuid(),
  activityId: z.string().uuid(),
  day: z.string().nullable(),
  dateCustom: z.string().nullable(),
  allDays: z.boolean(),
  times: z.array(
    availabilityTimeSchema.extend({
      id: z.string().uuid().optional(),
    }),
  ),
  createdAt: z.string().nullable(),
  updatedAt: z.string().nullable(),
  deletedAt: z.string().nullable(),
});
export type AvailabilityDTO = z.infer<typeof availabilityDtoSchema>;

export const availabilityUpsertSchema = z.object({
  id: z.string().uuid().optional(),
  /** One of `monday`..`sunday`, or null for `allDays`/`custom` rows. */
  day: z.enum(DAYS_OF_WEEK).nullable().optional(),
  /** ISO date (yyyy-mm-dd) for custom-date rows; null otherwise. */
  dateCustom: z.string().nullable().optional(),
  allDays: z.boolean().default(false),
  times: z.array(availabilityTimeSchema).default([]),
});
export type AvailabilityUpsertInput = z.infer<typeof availabilityUpsertSchema>;

/** Body of `PUT /activities/:id/availabilities` — full bulk replace. */
export const availabilitiesBulkUpsertSchema = z.object({
  availabilities: z.array(availabilityUpsertSchema),
});
export type AvailabilitiesBulkUpsertInput = z.infer<typeof availabilitiesBulkUpsertSchema>;
